import type { TimeGranularity } from '../types/api';

export interface ActivityBucketTotals {
  toolCalls: number;
  userTurns: number;
  aiTurns: number;
}

export const emptyActivityTotals: ActivityBucketTotals = {
  toolCalls: 0,
  userTurns: 0,
  aiTurns: 0,
};

// activityBucketKey maps a daily (YYYY-MM-DD, UTC) bucket string onto the
// bucket key for the requested chart granularity, mirroring the backend's
// bucketing rules (GetTimeSeriesWeekly/Monthly in db/queries.sql) so
// activity-series rows merge cleanly with the rest of the chart data. This
// mirrors tokenBucketKey in tokenBucketing.ts:
//  - daily: unchanged
//  - weekly: the Sunday on/before the date (DATE_TRUNC('week', d+1day)-1day)
//  - monthly: the 1st of that month
export function activityBucketKey(dailyBucket: string, granularity: TimeGranularity): string {
  if (granularity === 'daily') return dailyBucket;

  const [y, m, d] = dailyBucket.split('-').map((n) => parseInt(n, 10));
  const date = new Date(Date.UTC(y, m - 1, d));

  if (granularity === 'monthly') {
    date.setUTCDate(1);
  } else {
    // weekly: roll back to the most recent Sunday.
    date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  }

  return date.toISOString().slice(0, 10);
}

// aggregateActivitySeries buckets per-day activity rows (from
// GetActivitySeries) into the requested granularity, summing days that fall
// in the same bucket. This keeps the turns/tool-calls charts time-correct
// (sourced from the per-day rollup) at any granularity, not just daily.
// Mirrors aggregateTokenSeries in tokenBucketing.ts.
export function aggregateActivitySeries(
  dataPoints: Array<{
    bucket: string;
    toolCalls?: string;
    userTurns?: string;
    aiTurns?: string;
  }>,
  granularity: TimeGranularity
): Map<string, ActivityBucketTotals> {
  const result = new Map<string, ActivityBucketTotals>();
  for (const dp of dataPoints) {
    const key = activityBucketKey(dp.bucket, granularity);
    const existing = result.get(key) ?? { ...emptyActivityTotals };
    existing.toolCalls += parseInt(dp.toolCalls || '0', 10);
    existing.userTurns += parseInt(dp.userTurns || '0', 10);
    existing.aiTurns += parseInt(dp.aiTurns || '0', 10);
    result.set(key, existing);
  }
  return result;
}
